/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2026. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { ReduxUtils } from '@spm/core';

import {
  POST_VERIFICATIONS_CREATE_CASEWORKER_TASK,
  POST_VERIFICATIONS_LINK_FILE,
  DELETE_VERIFICATIONS_FILE_LINKS_BY_LINK_ID,
  FETCH_VERIFICATIONS,
  GET_VERIFICATIONS_BY_VERIFICATION_ID,
  POST_VERIFICATIONS_SD_CREATE_CASEWORKER_TASK,
} from './actionTypes';

import {
  postVerificationsCreateCaseworkerTaskUtil,
  postVerificationsLinkFileUtil,
  deleteVerificationsFileLinksByLinkIdUtil,
  fetchVerificationsUtil,
  getVerificationsByVerificationIdUtil,
  postVerificationsSdCreateCaseworkerTaskUtil,
} from './utils';

/**
 * Contains the action creator functions that update the Verifications slice of the store.
 *
 * @namespace VerificationsActions
 */
export default class actions {
  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/verifications/create_caseworker_task
   *
   * @param {NewCaseParticipantEvdUploadKey} data object that will be posted to the api
   * @param {function} callback function to call once this function has finished
   * @function
   * @memberof VerificationsActions
   */
  static postVerificationsCreateCaseworkerTask = (
    dispatch,
    newCaseParticipantEvdUploadKey,
    callback
  ) => {
    ReduxUtils.generateAction(
      dispatch,
      postVerificationsCreateCaseworkerTaskUtil.bind(null, newCaseParticipantEvdUploadKey),
      POST_VERIFICATIONS_CREATE_CASEWORKER_TASK,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to postVerificationsCreateCaseworkerTask.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof VerificationsActions
   */
  static resetPostVerificationsCreateCaseworkerTask = (dispatch, data) => {
    const json = {
      type: POST_VERIFICATIONS_CREATE_CASEWORKER_TASK.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to postVerificationsCreateCaseworkerTask.
   *
   * @param {function} dispatch
   * @function
   * @memberof VerificationsActions
   */
  static resetPostVerificationsCreateCaseworkerTaskError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, POST_VERIFICATIONS_CREATE_CASEWORKER_TASK.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/verifications/link_file
   *
   * @param {NewSPMVerificationFileLinkCreation} data object that will be posted to the api
   * @param {function} callback function to call once this function has finished
   * @function
   * @memberof VerificationsActions
   */
  static postVerificationsLinkFile = (dispatch, newSPMVerificationFileLinkCreation, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      postVerificationsLinkFileUtil.bind(null, newSPMVerificationFileLinkCreation),
      POST_VERIFICATIONS_LINK_FILE,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to postVerificationsLinkFile.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof VerificationsActions
   */
  static resetPostVerificationsLinkFile = (dispatch, data) => {
    const json = {
      type: POST_VERIFICATIONS_LINK_FILE.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to postVerificationsLinkFile.
   *
   * @param {function} dispatch
   * @function
   * @memberof VerificationsActions
   */
  static resetPostVerificationsLinkFileError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, POST_VERIFICATIONS_LINK_FILE.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/verifications/{verificationId}/file_links/{linkId}
   *
   * @param {string} verificationId.
   * @param {string} linkId.
   * @param {any} callback function to call once this function has finished
   * @function
   * @memberof VerificationsActions
   */
  static deleteVerificationsFileLinksByLinkId = (dispatch, verificationId, linkId, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      deleteVerificationsFileLinksByLinkIdUtil.bind(null, verificationId, linkId),
      DELETE_VERIFICATIONS_FILE_LINKS_BY_LINK_ID,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to deleteVerificationsFileLinksByLinkId.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof VerificationsActions
   */
  static resetDeleteVerificationsFileLinksByLinkId = (dispatch, data) => {
    const json = {
      type: DELETE_VERIFICATIONS_FILE_LINKS_BY_LINK_ID.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to deleteVerificationsFileLinksByLinkId.
   *
   * @param {function} dispatch
   * @function
   * @memberof VerificationsActions
   */
  static resetDeleteVerificationsFileLinksByLinkIdError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, DELETE_VERIFICATIONS_FILE_LINKS_BY_LINK_ID.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/verifications
   *
   * @param {string} caseId.
   * @param {string} personId.
   * @param {any} callback function to call once this function has finished
   * @function
   * @memberof VerificationsActions
   */
  static fetchVerifications = (dispatch, caseId, personId, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      fetchVerificationsUtil.bind(null, caseId, personId),

      FETCH_VERIFICATIONS,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to fetchVerifications.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof VerificationsActions
   */
  static resetFetchVerifications = (dispatch, data) => {
    const json = {
      type: FETCH_VERIFICATIONS.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to fetchVerifications.
   *
   * @param {function} dispatch
   * @function
   * @memberof VerificationsActions
   */
  static resetFetchVerificationsError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, FETCH_VERIFICATIONS.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/verifications/{verificationId}
   *
   * @param {string} verificationId.
   * @param {any} callback function to call once this function has finished
   * @function
   * @memberof VerificationsActions
   */
  static getVerificationsByVerificationId = (dispatch, verificationId, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      getVerificationsByVerificationIdUtil.bind(null, verificationId),

      GET_VERIFICATIONS_BY_VERIFICATION_ID,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to getVerificationsByVerificationId.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof VerificationsActions
   */
  static resetGetVerificationsByVerificationId = (dispatch, data) => {
    const json = {
      type: GET_VERIFICATIONS_BY_VERIFICATION_ID.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to getVerificationsByVerificationId.
   *
   * @param {function} dispatch
   * @function
   * @memberof VerificationsActions
   */
  static resetGetVerificationsByVerificationIdError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, GET_VERIFICATIONS_BY_VERIFICATION_ID.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/verifications/sd_create_caseworker_task
   *
   * @param {NewCaseParticipantEvdUploadKey} data object that will be posted to the api
   * @param {function} callback function to call once this function has finished
   * @function
   * @memberof VerificationsActions
   */
  static postVerificationsSdCreateCaseworkerTask = (
    dispatch,
    newCaseParticipantEvdUploadKey,
    callback
  ) => {
    ReduxUtils.generateAction(
      dispatch,
      postVerificationsSdCreateCaseworkerTaskUtil.bind(null, newCaseParticipantEvdUploadKey),
      POST_VERIFICATIONS_SD_CREATE_CASEWORKER_TASK,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to postVerificationsSdCreateCaseworkerTask.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof VerificationsActions
   */
  static resetPostVerificationsSdCreateCaseworkerTask = (dispatch, data) => {
    const json = {
      type: POST_VERIFICATIONS_SD_CREATE_CASEWORKER_TASK.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to postVerificationsSdCreateCaseworkerTask.
   *
   * @param {function} dispatch
   * @function
   * @memberof VerificationsActions
   */
  static resetPostVerificationsSdCreateCaseworkerTaskError = dispatch => {
    ReduxUtils.resetErrorAction(
      dispatch,
      POST_VERIFICATIONS_SD_CREATE_CASEWORKER_TASK.failureAction
    );
  };
}
